(() => {
  // Every module renders its own <script> tag, so with two modules this file runs twice.
  if (window.cartFeaturedProductsLoaded) return;
  window.cartFeaturedProductsLoaded = true;

  const selectors = {
    module: '[data-cart-featured-products]',
    slider: '[data-cfp-slider]',
    card: '[data-cfp-card]',
    reserve: '[data-cfp-reserve]',
    nav: '[data-cfp-nav]',
    prev: '[data-cfp-prev]',
    next: '[data-cfp-next]',
    select: '[data-cfp-select]',
    add: '[data-cfp-add]',
    cartItem: '#CartDrawer .cart__item',
    upsell: '#CartDrawer .upsell',
  };

  // ponytail: hardcoded like snippets/cart-item.liquid; make it a setting if another type needs priority
  const PRIORITY_TAG = 'Bralettes';
  // Fallback once the product type has nothing left: same list and order as
  // snippets/seo-meta-tags.liquid, first match wins.
  const CATEGORY_TAGS = ['Bralettes', 'Period', 'Panties', 'Swim Tops', 'Swim Bottoms', 'Swimsuits', 'nightwear'];

  const getCartItemElement = (item) => document.querySelector(`${selectors.cartItem}[data-key="${item.key}"]`);
  const getTags = (item) => getCartItemElement(item)?.dataset.tags?.split(',') ?? [];

  // Where an item's recommendations come from, best first: every product of its exact
  // type (Shopify's built-in /collections/types), then every product with its category tag.
  const getSources = (item) => {
    const tags = getTags(item);
    const tag = CATEGORY_TAGS.find((categoryTag) => tags.includes(categoryTag));

    return [
      item.product_type && `collections/types?q=${encodeURIComponent(item.product_type)}`,
      tag && `collections/all/${tag.toLowerCase().replace(/\s+/g, '-')}`,
    ].filter(Boolean);
  };

  // Recommendations follow the latest added item (the cart lists it first), or the latest
  // bralette whenever the cart has one (by tag, then by product type).
  // Excluded products (theme setting) are never the reference.
  const getReferenceItem = (items, excluded) => {
    const candidates = items.filter(
      (item) => !excluded.includes(String(item.product_id)) && getSources(item).length,
    );
    const isBralette = (item) => getTags(item).includes(PRIORITY_TAG) || /bralette/i.test(item.product_type);

    return candidates.find(isBralette) ?? candidates[0];
  };

  // "Complete your set" offers the set of the newest item. That step is done once the item
  // has a partner of the same style and the other type (top/bottom) in the cart, the same
  // pairing cart-drawer-ds.js groups into sets, or when there is no set to offer at all:
  // no style- tag, or the upsell block came back without that style.
  const isSetStepDone = (items) => {
    const [latest, ...rest] = items.map(getCartItemElement);
    const style = latest?.dataset.style;
    if (!style) return true;

    const hasPartner = rest.some(
      (element) => element?.dataset.style === style && element.dataset.itemType !== latest.dataset.itemType,
    );
    if (hasPartner) return true;

    // upsell-v2-ds.js shows a preloader while loading; an empty result hides the block
    // but leaves the previous content in place.
    const upsell = document.querySelector(selectors.upsell);
    if (!upsell || upsell.classList.contains('hidden')) return true;
    if (upsell.classList.contains('processing')) return false;

    return !upsell.querySelector(
      `[data-upsell-selector="set-wrapper"][data-set="${style}"] [data-upsell-selector="product"]`,
    );
  };

  // Product cards rendered by templates/collection.cart-recommendations.liquid
  const fetchCards = async (path) => {
    const url = new URL(`${window.Shopify.routes.root}${path}`, window.location.origin);
    url.searchParams.set('view', 'cart-recommendations');

    const response = await fetch(url);
    if (!response.ok) throw new Error(`${url} responded ${response.status}`);

    const template = document.createElement('template');
    template.innerHTML = await response.text();

    return [...template.content.querySelectorAll(selectors.card)];
  };

  const addToCart = async (variantId) => {
    const request = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });

    if (!request.ok) throw new Error(`cart/add.js responded ${request.status}`);

    const data = await request.json();

    document.dispatchEvent(
      new CustomEvent('ajaxProduct:added', { detail: { product: data?.items?.[0] } }),
    );
  };

  class CartFeaturedProducts {
    constructor(element) {
      this.element = element;
      this.nav = element.querySelector(selectors.nav);
      this.limit = Number(element.dataset.cfpLimit) || 0;
      this.source = element.dataset.cfpSource;
      this.excluded = element.dataset.cfpExcluded?.split(',') ?? [];
      this.gated = element.hasAttribute('data-cfp-gated');
      // Fetched cards per source, kept for the session
      this.pools = new Map();
      this.run = 0;
      this.items = null;

      // Every rendered product in collection order: the visible row plus the reserve
      // (which also holds products that were in the cart at render).
      const reserve = element.querySelector(selectors.reserve);
      this.pool = [...this.cards, ...(reserve?.content.children ?? [])].sort(
        (a, b) => a.dataset.cfpIndex - b.dataset.cfpIndex,
      );
      reserve?.remove();

      element.addEventListener('click', (event) => {
        const button = event.target.closest(selectors.add);
        if (button) this.add(button);
      });

      const isSingle = element.classList.contains('cfp--single');
      const prevEl = element.querySelector(selectors.prev);
      const nextEl = element.querySelector(selectors.next);

      this.swiper = new Swiper(element.querySelector(selectors.slider), {
        // 'auto' reads the shelf card's fractional CSS width. Single slides are
        // full-bleed, so Swiper sizes them — a CSS width:100% there resolves against
        // a container whose own width depends on the slides, and the track blows up.
        slidesPerView: isSingle ? 1 : 'auto',
        slidesPerGroup: 1,
        spaceBetween: isSingle ? 26 : 12,
        // The drawer is closed on load, so the slider measures 0 until it opens.
        observer: true,
        observeParents: true,
        mousewheel: { forceToAxis: true },
        // Swiper's a11y module would otherwise replace the translated aria-labels.
        a11y: {
          prevSlideMessage: prevEl.getAttribute('aria-label'),
          nextSlideMessage: nextEl.getAttribute('aria-label'),
        },
        navigation: {
          prevEl,
          nextEl,
          // The theme's .swiper-button-disabled hides the arrow; the design greys it out.
          disabledClass: 'cfp__arrow--disabled',
          lockClass: 'cfp__arrow--lock',
        },
        breakpoints: {
          577: { spaceBetween: isSingle ? 34 : 14 },
        },
        on: {
          // A single slide has nothing to navigate to, so the arrows go away.
          init: (swiper) => this.toggleNav(swiper),
          lock: (swiper) => this.toggleNav(swiper),
          unlock: (swiper) => this.toggleNav(swiper),
          resize: (swiper) => this.toggleNav(swiper),
        },
      });

      // #CartDrawer is display:none until opened, so Swiper initialises at 0px and
      // observer:true does not reliably catch the reveal; the theme's drawerOpen does.
      document.addEventListener('drawerOpen', () => {
        this.swiper.update();
        this.toggleNav(this.swiper);
      });

      // "Complete your set" reloads after cart changes; recheck once it settles.
      const upsell = document.querySelector(selectors.upsell);
      if (this.gated && upsell) {
        new MutationObserver(() => this.updateVisibility()).observe(upsell, { attributeFilter: ['class'] });
      }
    }

    get cards() {
      return [...this.element.querySelectorAll(selectors.card)];
    }

    toggleNav(swiper) {
      if (this.nav) this.nav.hidden = swiper.isLocked || this.cards.length < 2;
    }

    async add(button) {
      const card = button.closest(selectors.card);
      const select = card.querySelector(selectors.select);
      const variantId = button.dataset.variantId || select?.value;

      // No size picked yet — send the customer to the selector instead of failing silently.
      if (!variantId) {
        select?.focus();
        return;
      }

      button.disabled = true;
      this.element.classList.add('processing');

      try {
        await addToCart(Number(variantId));
      } catch (error) {
        console.error(error);
        button.disabled = false;
        this.element.classList.remove('processing');
      }
    }

    poolFor(path) {
      if (!this.pools.has(path)) {
        const pool = fetchCards(path).catch((error) => {
          console.error(error);
          this.pools.delete(path);
          return [];
        });
        this.pools.set(path, pool);
      }

      return this.pools.get(path);
    }

    updateVisibility() {
      if (!this.items) return;

      this.element.hidden = this.cards.length === 0 || (this.gated && !isSetStepDone(this.items));
      this.swiper.update();
      this.toggleNav(this.swiper);
    }

    // The row is always the first `limit` pool products not in the cart: an added
    // product leaves and the next one takes its place, a removed one returns to its
    // spot, and the module hides while every product is in the cart.
    async syncWithCart(cart) {
      const items = cart?.items ?? [];
      const inCart = new Set(items.map((item) => item.product_id));
      const getRow = (pool) =>
        pool.filter((card) => !inCart.has(Number(card.dataset.productId))).slice(0, this.limit);

      if (this.source === 'product_type') {
        this.run += 1;
        const run = this.run;
        const reference = getReferenceItem(items, this.excluded);
        let pool = [];

        // The next source only when this one has nothing left outside the cart
        for (const path of reference ? getSources(reference) : []) {
          pool = await this.poolFor(path);
          if (getRow(pool).length) break;
        }

        // A later cart change has already taken over
        if (run !== this.run) return;
        this.pool = pool;
      }

      const row = getRow(this.pool);
      const current = this.cards;
      const changed =
        row.length !== current.length || row.some((card, index) => card !== current[index]);
      if (changed) this.swiper.wrapperEl.replaceChildren(...row);

      this.element.classList.remove('processing');
      this.element.querySelectorAll(selectors.add).forEach((button) => (button.disabled = false));

      this.items = items;
      this.updateVisibility();
    }
  }

  const instances = [...document.querySelectorAll(selectors.module)].map(
    (element) => new CartFeaturedProducts(element),
  );

  document.addEventListener('cart:markup-built', (event) => {
    instances.forEach((instance) => instance.syncWithCart(event.detail?.cart));
  });
})();
